import { useEffect, useRef, useState } from 'react';
import { StanLoader } from '@/components';
import { AffiliateRegistrationForm } from '@/features/auth/components/AffiliateRegistrationForm';
import { AffiliateLoginForm } from '@/features/auth/components/AffiliateLoginForm';
import { useAffiliateToken } from '@/features/auth/hooks/useAffiliateToken';
import { useSessionCountry } from '@/features/auth/hooks/useSessionCountry';
import { getGDPRComplianceDocument } from '@/features/auth/utils/gdpr';
import { useStanRouter } from '@/hooks/useStanRouter';
import { stanNotify } from '@/lib';
import { stanAnalytics } from '@/lib/stanAnalytics';
import { LEGAL_URLS } from '@/lib/constants';
import Logo from '@/assets/images/stan-logo.svg';

type Step = 'choice' | 'register' | 'login';

const CompleteAffiliateRegistrationPage = () => {
    const { push } = useStanRouter();
    const { token, status, affiliateUser, isLoading, error } = useAffiliateToken();
    const { country } = useSessionCountry();

    const [currentStep, setCurrentStep] = useState<Step>('choice');

    const hasHandledErrorRef = useRef(false);

    // Get GDPR compliance document URL if user is in EU/UK
    const gdprComplianceDocument = getGDPRComplianceDocument(country);

    // Handle invalid/expired token - show toast and redirect
    useEffect(() => {
        if (hasHandledErrorRef.current) return;

        // Determine error message based on status
        let errorTitle = '';
        let errorText = '';

        if (status === 'missing' || error) {
            errorTitle = 'Error';
            errorText = 'Invalid registration link';
        } else if (status === 'expired') {
            errorTitle = 'Complete Registration Link Expired';
            errorText = 'Please reach out to creator to send another link';
        }

        if (errorTitle) {
            hasHandledErrorRef.current = true;
            // Small delay ensures toast renders before navigation
            setTimeout(() => {
                stanNotify({
                    type: 'error',
                    title: errorTitle,
                    text: errorText,
                    duration: 5000,
                });
                push('/login');
            }, 100);
        }
    }, [status, error, push]);

    // Track analytics on mount
    useEffect(() => {
        stanAnalytics('affiliate-complete-registration', {
            meta: { username: 'guest' },
        });
    }, []);

    const handleSuccess = () => {
        push('/login');
    };

    const handleAlreadyHaveAccount = () => {
        setCurrentStep('login');
    };

    const handleCreateNewAccount = () => {
        setCurrentStep('register');
    };

    // Show loading state
    if (isLoading || status === 'missing' || status === 'expired') {
        return (
            <div className='flex min-h-svh items-center justify-center'>
                <StanLoader />
            </div>
        );
    }

    return (
        <div className='mx-auto w-full max-w-md px-8 pt-5'>
            {/* Header */}
            <div className='mb-6 flex flex-col items-center gap-4'>
                <Logo className='size-11' />
                <h1 className='text-heading-h2 text-dark text-center'>Get Started as a Stan Affiliate</h1>
            </div>

            {/* Step 1: Choice */}
            {currentStep === 'choice' && (
                <div className='my-8'>
                    <button
                        type='button'
                        onClick={handleAlreadyHaveAccount}
                        className='bg-brand-main text-on-brand hover:bg-brand-dark w-full rounded-lg py-4 text-center font-semibold transition-colors'
                    >
                        I already have a Stan Account
                    </button>
                    <p className='text-body-sm text-gray mt-6 text-center'>
                        Don&apos;t have an account? <br />
                        <button
                            type='button'
                            onClick={handleCreateNewAccount}
                            className='text-dark cursor-pointer font-semibold underline'
                        >
                            Click here to get an Affiliate Account now
                        </button>
                    </p>
                </div>
            )}

            {/* Step 2: Register Form */}
            {currentStep === 'register' && token && (
                <>
                    <AffiliateRegistrationForm
                        affiliateUser={affiliateUser}
                        token={token}
                        onSuccess={handleSuccess}
                        defaultCountry={country}
                    />

                    {/* Terms of Service */}
                    <div className='mt-6 text-center'>
                        <p className='text-body-sm text-gray'>
                            By signing up, you agree to our <br />
                            <a
                                href={LEGAL_URLS.TERMS_OF_SERVICE}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='font-medium underline focus-visible:ring-2'
                            >
                                Terms of Service
                            </a>{' '}
                            and{' '}
                            <a
                                href={LEGAL_URLS.PRIVACY_POLICY}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='font-medium underline focus-visible:ring-2'
                            >
                                Privacy Policy.
                            </a>
                            {gdprComplianceDocument && (
                                <>
                                    {' '}
                                    Our GDPR compliance{' '}
                                    <a
                                        href={gdprComplianceDocument}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className='font-medium underline focus-visible:ring-2'
                                    >
                                        certificate
                                    </a>{' '}
                                    is available for review.
                                </>
                            )}
                        </p>
                    </div>

                    {/* Login Link */}
                    <div className='mt-5 text-center'>
                        <p className='text-body-sm text-gray'>
                            Already have an account?{' '}
                            <button
                                type='button'
                                onClick={handleAlreadyHaveAccount}
                                className='text-dark cursor-pointer font-semibold underline'
                            >
                                Login
                            </button>
                        </p>
                    </div>
                </>
            )}

            {/* Step 3: Login Form */}
            {currentStep === 'login' && token && (
                <>
                    <AffiliateLoginForm token={token} onSuccess={handleSuccess} />

                    {/* Create Account Link */}
                    <div className='mt-6 text-center'>
                        <p className='text-body-sm text-gray'>
                            Don&apos;t have an account? <br />
                            <button
                                type='button'
                                onClick={handleCreateNewAccount}
                                className='text-dark cursor-pointer font-semibold underline'
                            >
                                Click here to get an Affiliate Account now
                            </button>
                        </p>
                    </div>
                </>
            )}
        </div>
    );
};

export default CompleteAffiliateRegistrationPage;
